<template>
  <div class="bg-white rounded-2xl shadow-md p-6 space-y-4">
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-bold">Coordonnées</h2>
      <StoreConfigurationStatusBadge :configuration-status="store.contactInformationsConfigStatus" />
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
      <StackedField label="Email du magasin">
        <Checkbox
          class="mb-2"
          id="noSellerEmail"
          name="noSellerEmail"
          :label="t('no_seller_email_label')"
          labelClass="font-normal"
          v-model="noSellerEmail"
        />
        <template v-if="!noSellerEmail">
          <Loading v-if="loadingUsers" />
          <template v-else-if="!sellersProfileIdOptions.length">
            <div class="text-sm text-gray-500 mb-1">Renseignez l'adresse email à laquelle les vendeurs ont accès.</div>
            <FormField id="sellerEmail" name="email" placeholder="john@doe.com" v-model="formData.sellerEmail" />
          </template>
          <template v-else>
            <div class="text-sm text-gray-500 mb-1">Sélectionnez un utilisateur avec le rôle vendeur</div>
            <Selector
              id="sellersProfileId"
              name="sellersProfileId"
              :options="sellersProfileIdOptions"
              v-model="formData.sellersProfileId"
            />
          </template>
        </template>
      </StackedField>
    </div>

    <hr class="my-3" />
    <p class="font-bold text-lg mb-2">Référent</p>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
      <StackedField label="Nom">
        <FormField id="referent_last_name" name="referent_last_name" v-model="formData.referent.lastName" />
      </StackedField>
      <StackedField label="Prénom">
        <FormField id="referent_first_name" name="referent_first_name" v-model="formData.referent.firstName" />
      </StackedField>
      <StackedField label="Email">
        <FormField id="referent_email" name="referent_email" placeholder="Email" v-model="formData.referent.email" />
      </StackedField>
      <StackedField label="Téléphone">
        <FormField
          id="referent_phone"
          name="referent_phone"
          placeholder="06 12 34 56 78"
          v-model="formData.referent.phone"
        />
      </StackedField>
      <StackedField label="Poste du référent">
        <Selector id="referentRole" name="referentRole" :options="roles" v-model="formData.referentRole" />
      </StackedField>
    </div>

    <div class="flex justify-end gap-x-3">
      <SecondaryButton @click="$emit('previous')">Retour</SecondaryButton>
      <SecondaryButton @click="submit" :loading="saving">Continuer</SecondaryButton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { StackedField, FormField, Selector, SecondaryButton, Loading, Checkbox } from "@estaly/ui";
import { useI18n } from "vue-i18n";
import StoreConfigurationStatusBadge from "@/components/StoreConfigurationStatusBadge.vue";

export default {
  name: "StoreOnboardingStepContacts",
  components: { StackedField, FormField, Selector, SecondaryButton, Loading, StoreConfigurationStatusBadge, Checkbox },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: { store: { type: Object, required: true } },
  emits: ["step-completed", "previous"],
  data() {
    return {
      saving: false,
      loadingUsers: false,
      noSellerEmail: false,
      formData: {
        sellersProfileId: this.store.sellersProfile?.id || null,
        sellerEmail: this.store.sellersProfile?.email || null,
        referent: {
          lastName: this.store.referent?.lastName || "",
          firstName: this.store.referent?.firstName || "",
          email: this.store.referent?.email || "",
          phone: this.store.referent?.phone || "",
        },
        referentRole: this.store.referentRole || "",
      },
      roles: [],
    };
  },
  async mounted() {
    this.roles = [
      { value: "store_manager", label: this.$t("store.referent_role_values.store_manager") },
      { value: "director", label: this.$t("store.referent_role_values.director") },
      { value: "franchise_owner", label: this.$t("store.referent_role_values.franchise_owner") },
    ];
    await this.refreshStoreUsers();
  },
  computed: {
    ...mapState("merchantProfiles", { users: (state) => state.index }),
    sellers() {
      return this.users.filter((u) => u.role === "seller");
    },
    sellersProfileIdOptions() {
      return this.sellers.map((u) => ({ value: u.id, label: u.email }));
    },
    selectedSellerProfile() {
      return this.sellers.find((u) => u.id === this.formData.sellersProfileId);
    },
  },
  watch: {
    noSellerEmail(checked) {
      if (checked) {
        this.formData.sellerEmail = null;
        this.formData.sellersProfileId = null;
      }
    },
    "formData.sellerEmail"(val) {
      if (val && this.formData.sellersProfileId) this.formData.sellersProfileId = null;
    },
    "formData.sellersProfileId"(val) {
      if (val && this.formData.sellerEmail) this.formData.sellerEmail = null;
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("merchantProfiles", ["fetchIndex"]),
    ...mapActions("stores", ["updateContactInformations"]),
    async refreshStoreUsers() {
      this.loadingUsers = true;
      await this.fetchIndex({ storeIds: [this.store.id], roles: ["seller"] });
      this.loadingUsers = false;
    },
    async submit() {
      const email = this.noSellerEmail ? null : this.selectedSellerProfile?.email || this.formData.sellerEmail;
      if (!this.noSellerEmail && !email) {
        await this.notify({
          category: "simple",
          type: "error",
          title: "Adresse email vendeur",
          text: "Saisissez l'email vendeur ou cochez la case pour indiquer qu'il n'y en a pas.",
        });
        return;
      }
      this.saving = true;
      const response = await this.updateContactInformations({
        storeId: this.store.id,
        referentRole: this.formData.referentRole,
        email,
        referent: {
          lastName: this.formData.referent?.lastName,
          firstName: this.formData.referent?.firstName,
          email: this.formData.referent?.email,
          phone: this.formData.referent?.phone,
        },
      });
      this.saving = false;
      if (!response?.success) return;

      await this.notify({ category: "simple", type: "success", title: "Coordonnées enregistrées" });
      this.$emit("step-completed");
    },
  },
};
</script>

<i18n src="./StoreOnboardingStepContacts.i18n.json"></i18n>
